import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  PawPrint,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  MapPin,
  Calendar,
  Activity,
  Camera,
  Trash2,
  MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Animal, Enclosure } from '../LivestockManagementPage';

// Import design system tokens
import { 
  getCardClasses, 
  getShadowClasses, 
  getButtonClasses,
  components, 
  colors, 
  typography,
  spacing
} from '@/components/business/design-system/businessDesignTokens';

interface AnimalsTabProps {
  animals: Animal[];
  setAnimals: React.Dispatch<React.SetStateAction<Animal[]>>;
  enclosures: Enclosure[];
}

export function AnimalsTab({ animals, setAnimals, enclosures }: AnimalsTabProps) {
  const { t } = useTranslation('business', { keyPrefix: 'livestock.animalsTab' });
  const { t: tc } = useTranslation('common');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState<Animal | null>(null);
  
  // Form state for adding/editing animals
  const [formData, setFormData] = useState<Partial<Animal>>({
    name: '',
    species: '',
    sex: 'Female',
    age: '',
    dateOfBirth: '',
    microchipId: '',
    intake: {
      date: '',
      origin: '',
      reason: ''
    },
    currentLocation: '',
    status: 'Healthy',
    physicalCharacteristics: {
      weight: 0,
      height: 0,
      markings: '',
      color: ''
    },
    notes: '',
    photos: []
  });

  // Get unique values for filters
  const uniqueSpecies = [...new Set(animals.map(a => a.species))];
  const uniqueStatuses = [...new Set(animals.map(a => a.status))];
  const uniqueLocations = [...new Set(animals.map(a => a.currentLocation))];

  // Filter animals
  const filteredAnimals = animals.filter(animal => {
    const matchesSearch = searchTerm === '' || 
      animal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      animal.species.toLowerCase().includes(searchTerm.toLowerCase()) ||
      animal.microchipId?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSpecies = speciesFilter === 'all' || animal.species === speciesFilter;
    const matchesStatus = statusFilter === 'all' || animal.status === statusFilter;
    const matchesLocation = locationFilter === 'all' || animal.currentLocation === locationFilter;
    
    return matchesSearch && matchesSpecies && matchesStatus && matchesLocation;
  });

  const handleFormChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof Animal] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSaveAnimal = () => {
    if (!formData.name || !formData.species) {
      alert('Please fill in required fields');
      return;
    }

    if (showEditDialog && selectedAnimal) {
      // Update existing animal
      setAnimals(prev => 
        prev.map(animal => 
          animal.id === selectedAnimal.id 
            ? { ...animal, ...formData } as Animal
            : animal
        )
      );
    } else {
      // Add new animal
      const newAnimal: Animal = {
        ...formData,
        id: `a${Date.now()}`,
        photos: formData.photos || []
      } as Animal;
      
      setAnimals(prev => [...prev, newAnimal]);
    }

    // Reset form and close dialog
    setFormData({
      name: '',
      species: '',
      sex: 'Female',
      age: '',
      dateOfBirth: '',
      microchipId: '',
      intake: { date: '', origin: '', reason: '' },
      currentLocation: '',
      status: 'Healthy',
      physicalCharacteristics: { weight: 0, height: 0, markings: '', color: '' },
      notes: '',
      photos: []
    });
    setShowAddDialog(false);
    setShowEditDialog(false);
    setSelectedAnimal(null);
  };

  const handleDeleteAnimal = (animalId: string) => {
    if (confirm(t('confirmDeleteAnimal'))) {
      setAnimals(prev => prev.filter(animal => animal.id !== animalId));
    }
  };

  const openEditDialog = (animal: Animal) => {
    setSelectedAnimal(animal);
    setFormData({ ...animal });
    setShowEditDialog(true);
  };

  const openViewDialog = (animal: Animal) => {
    setSelectedAnimal(animal);
    setShowViewDialog(true);
  };

  const getStatusColor = (status: Animal['status']) => {
    switch (status) {
      case 'Healthy': return 'default';
      case 'Under Treatment': return 'secondary';
      case 'Quarantine': return 'outline';
      case 'Critical': return 'destructive';
      default: return 'secondary';
    }
  };

  return (
    <div className={spacing.sectionGap}>
      {/* Streamlined Header */}
      <div className="mb-6">
        <p className="text-sm text-slate-600">{t('description')}</p>
      </div>

      {/* Streamlined Search and Filters */}
      <div className="space-y-4">
        {/* Compact Search and Actions Row */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto">
            {/* Search Input */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, species, or microchip..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Filter Dropdowns */}
            <div className="flex gap-2">
              <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Species" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Species</SelectItem>
                  {uniqueSpecies.map(species => (
                    <SelectItem key={species} value={species}>{species}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {uniqueStatuses.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {uniqueLocations.map(location => (
                    <SelectItem key={location} value={location}>{location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Add Button */}
          <Button onClick={() => setShowAddDialog(true)} className={getButtonClasses('success')}>
            <Plus className="w-4 h-4 mr-2" />
            {t('addNewAnimal')}
          </Button>
        </div>

        {/* Results Count and Clear Filters */}
        <div className="flex items-center justify-between text-sm py-2 border-t border-slate-100">
          <span className="text-slate-600">
            Showing {filteredAnimals.length} of {animals.length} animals
          </span>
          {(searchTerm || speciesFilter !== 'all' || statusFilter !== 'all' || locationFilter !== 'all') && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setSpeciesFilter('all');
                setStatusFilter('all');
                setLocationFilter('all');
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Animals Table */}
      <Card className={getCardClasses('basic')}>
        <CardHeader>
          <CardTitle>Animal Records</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Animal</TableHead>
                  <TableHead>Species</TableHead>
                  <TableHead>Age/Sex</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Intake Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAnimals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex flex-col items-center space-y-2">
                        <PawPrint className="h-8 w-8 text-slate-400" />
                        <p className="font-medium text-slate-900">No animals found</p>
                        <p className="text-sm text-slate-600">
                          {animals.length === 0 
                            ? "Add your first animal to get started"
                            : "Try adjusting your search criteria"
                          }
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAnimals.map((animal) => (
                    <TableRow key={animal.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                            <PawPrint className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{animal.name}</p>
                            <p className="text-xs text-slate-500">{animal.microchipId || 'No chip'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{animal.species}</TableCell>
                      <TableCell>
                        <div>
                          <p>{animal.age}</p>
                          <p className="text-sm text-slate-600">{animal.sex}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          <span className="text-sm">{animal.currentLocation}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(animal.status)}>
                          {animal.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3 text-slate-400" />
                          <span className="text-sm">
                            {new Date(animal.intake.date).toLocaleDateString()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openViewDialog(animal)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(animal)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDeleteAnimal(animal.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Animal Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setShowEditDialog(false);
          setSelectedAnimal(null);
          setFormData({
            name: '', species: '', sex: 'Female', age: '', dateOfBirth: '',
            microchipId: '', intake: { date: '', origin: '', reason: '' },
            currentLocation: '', status: 'Healthy',
            physicalCharacteristics: { weight: 0, height: 0, markings: '', color: '' },
            notes: '', photos: []
          });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEditDialog ? `Edit ${selectedAnimal?.name}` : 'Add New Animal'}
            </DialogTitle>
            <DialogDescription>
              {showEditDialog 
                ? 'Update the animal information below'
                : 'Fill in the details for the new animal'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-900">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="Animal name"
                  />
                </div>
                <div>
                  <Label htmlFor="species">Species *</Label>
                  <Input
                    id="species"
                    value={formData.species || ''}
                    onChange={(e) => handleFormChange('species', e.target.value)}
                    placeholder="e.g., African Elephant"
                  />
                </div>
                <div>
                  <Label htmlFor="sex">Sex</Label>
                  <Select value={formData.sex} onValueChange={(value) => handleFormChange('sex', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="age">Age</Label>
                  <Input
                    id="age"
                    value={formData.age || ''}
                    onChange={(e) => handleFormChange('age', e.target.value)}
                    placeholder="e.g., 5 years"
                  />
                </div>
                <div>
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth || ''}
                    onChange={(e) => handleFormChange('dateOfBirth', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="microchipId">Microchip ID</Label>
                  <Input
                    id="microchipId"
                    value={formData.microchipId || ''}
                    onChange={(e) => handleFormChange('microchipId', e.target.value)}
                    placeholder="Microchip number"
                  />
                </div>
              </div>
            </div>

            {/* Intake Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-900">Intake Information</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="intakeDate">Intake Date</Label>
                    <Input
                      id="intakeDate"
                      type="date"
                      value={formData.intake?.date || ''}
                      onChange={(e) => handleFormChange('intake.date', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="origin">Origin</Label>
                    <Input
                      id="origin"
                      value={formData.intake?.origin || ''}
                      onChange={(e) => handleFormChange('intake.origin', e.target.value)}
                      placeholder="Where did the animal come from?"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="reason">Intake Reason</Label>
                  <Textarea
                    id="reason"
                    value={formData.intake?.reason || ''}
                    onChange={(e) => handleFormChange('intake.reason', e.target.value)}
                    placeholder="Why was the animal brought to the sanctuary?"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Current Status */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-900">Current Status</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="currentLocation">Current Location</Label>
                  <Select 
                    value={formData.currentLocation} 
                    onValueChange={(value) => handleFormChange('currentLocation', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select enclosure" />
                    </SelectTrigger>
                    <SelectContent>
                      {enclosures.map(enclosure => (
                        <SelectItem key={enclosure.id} value={enclosure.name}>
                          {enclosure.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Health Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(value) => handleFormChange('status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Healthy">Healthy</SelectItem>
                      <SelectItem value="Under Treatment">Under Treatment</SelectItem>
                      <SelectItem value="Quarantine">Quarantine</SelectItem>
                      <SelectItem value="Critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Physical Characteristics */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-900">Physical Characteristics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    value={formData.physicalCharacteristics?.weight || ''}
                    onChange={(e) => handleFormChange('physicalCharacteristics.weight', parseInt(e.target.value) || 0)}
                    placeholder="Weight in kg"
                  />
                </div>
                <div>
                  <Label htmlFor="height">Height (cm)</Label>
                  <Input
                    id="height"
                    type="number"
                    value={formData.physicalCharacteristics?.height || ''}
                    onChange={(e) => handleFormChange('physicalCharacteristics.height', parseInt(e.target.value) || 0)}
                    placeholder="Height in cm"
                  />
                </div>
                <div>
                  <Label htmlFor="color">Color</Label>
                  <Input
                    id="color"
                    value={formData.physicalCharacteristics?.color || ''}
                    onChange={(e) => handleFormChange('physicalCharacteristics.color', e.target.value)}
                    placeholder="Primary color/pattern"
                  />
                </div>
                <div>
                  <Label htmlFor="markings">Distinctive Markings</Label>
                  <Input
                    id="markings"
                    value={formData.physicalCharacteristics?.markings || ''}
                    onChange={(e) => handleFormChange('physicalCharacteristics.markings', e.target.value)}
                    placeholder="Scars, marks, etc."
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder="Any additional information about the animal..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setShowEditDialog(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveAnimal} className="bg-emerald-600 hover:bg-emerald-700">
              {showEditDialog ? 'Update Animal' : 'Add Animal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Animal Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <PawPrint className="w-5 h-5 text-emerald-600" />
              <span>{selectedAnimal?.name}</span>
            </DialogTitle>
            <DialogDescription>Complete animal profile and history</DialogDescription>
          </DialogHeader>

          {selectedAnimal && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Basic Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-slate-600">Species:</span> {selectedAnimal.species}</div>
                    <div><span className="text-slate-600">Sex:</span> {selectedAnimal.sex}</div>
                    <div><span className="text-slate-600">Age:</span> {selectedAnimal.age}</div>
                    <div><span className="text-slate-600">Microchip:</span> {selectedAnimal.microchipId || 'None'}</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Current Status</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-slate-600">Location:</span> {selectedAnimal.currentLocation}
                    </div>
                    <div>
                      <span className="text-slate-600">Status:</span>{' '}
                      <Badge variant={getStatusColor(selectedAnimal.status)}>
                        {selectedAnimal.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-slate-900 mb-2">Intake Information</h3>
                <div className="text-sm space-y-1">
                  <div><span className="text-slate-600">Date:</span> {new Date(selectedAnimal.intake.date).toLocaleDateString()}</div>
                  <div><span className="text-slate-600">Origin:</span> {selectedAnimal.intake.origin}</div>
                  <div><span className="text-slate-600">Reason:</span> {selectedAnimal.intake.reason}</div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-slate-900 mb-2">Physical Characteristics</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-600">Weight:</span> {selectedAnimal.physicalCharacteristics.weight || 'Not recorded'} kg</div>
                  <div><span className="text-slate-600">Height:</span> {selectedAnimal.physicalCharacteristics.height || 'Not recorded'} cm</div>
                  <div><span className="text-slate-600">Color:</span> {selectedAnimal.physicalCharacteristics.color || 'Not recorded'}</div>
                  <div><span className="text-slate-600">Markings:</span> {selectedAnimal.physicalCharacteristics.markings || 'None noted'}</div>
                </div>
              </div>

              {selectedAnimal.notes && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Notes</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md">
                    {selectedAnimal.notes}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowViewDialog(false);
              if (selectedAnimal) openEditDialog(selectedAnimal);
            }}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Animal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 